export type AdminUser = {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  slackUserId?: string;
  isActive?: boolean;
  lastActive?: string | null;
  createdAt?: string;
  permissions: AdminPermission[];
};

export type CreateAdminUserDto = {
  firstName: string;
  lastName: string;
  email: string;
  slackUserId?: string;
  permissions?: AdminPermission[];
};

export type UpdateAdminUserDto = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  slackUserId?: string;
  permissions: AdminPermission[];
};

export enum AdminPermission {
  READ_ALOHA_PROVISIONING = 'readAlohaProvisioning',
  PAUSE_ALOHA_PROVISIONING = 'pauseAlohaProvisioning',
  RECOVER_ALOHA_PROVISIONING = 'recoverAlohaProvisioning',
  REVIEW_ALOHA_PROVISIONING = 'reviewAlohaProvisioning',
  ASSIGN_ALOHA_PROVISIONING = 'assignAlohaProvisioning',
  TAKEOVER_ALOHA_PROVISIONING = 'takeoverAlohaProvisioning',

  READ_CREDENTIALING = 'readCredentialing',
  REVIEW_CREDENTIALING = 'reviewCredentialing',
  OPERATE_CREDENTIALING = 'operateCredentialing',

  // CHATS
  READ_CHAT_GENERAL_ADMIN = 'readGeneralAdminChat',
  WRITE_CHAT_GENERAL_ADMIN = 'writeGeneralAdminChat',
  READ_CHAT_RBT_ADMIN = 'readRbtAdminChat',
  WRITE_CHAT_RBT_ADMIN = 'writeRbtAdminChat',
  READ_CHAT_CLIENT_ACQ_ADMIN = 'readClientAcqAdminChat',
  WRITE_CHAT_CLIENT_ACQ_ADMIN = 'writeClientAcqAdminChat',
  READ_CHAT_INS_OPS_ADMIN = 'readInsOpsAdminChat',
  WRITE_CHAT_INS_OPS_ADMIN = 'writeInsOpsAdminChat',
  READ_CHAT_TECH_ADMIN = 'readTechAdminChat',
  WRITE_CHAT_TECH_ADMIN = 'writeTechAdminChat',
  READ_CHAT_CLINICAL_ADMIN = 'readClinicalAdminChat',
  WRITE_CHAT_CLINICAL_ADMIN = 'writeClinicalAdminChat',
  READ_CHAT_CLIENT = 'readClientChat',
  WRITE_CHAT_CLIENT = 'writeClientChat',

  // PAGES
  READ_PAGE_MANAGE_CLIENTS = 'readPageManageClients',
  WRITE_PAGE_MANAGE_CLIENTS = 'writePageMangeClient',
  READ_PAGE_MANAGE_PROVIDERS = 'readPageManageProviders',
  WRITE_PAGE_MANAGE_PROVIDERS = 'writePageMangeProviders',
  READ_PAGE_MANAGE_REFERRALS = 'readPageManageReferrals',
  WRITE_PAGE_MANAGE_REFERRALS = 'writePageMangeReferrals',
  READ_PAGE_MANAGE_AUTH_REQUEST = 'readPageManageAuthRequest',
  WRITE_PAGE_MANAGE_AUTH_REQUEST = 'writePageMangeAuthRequest',
  READ_PAGE_MANAGE_RBT = 'readPageManage',
  WRITE_PAGE_MANAGE_RBT = 'writePageManage',
  READ_PAGE_MANAGE_BULLETIN = 'readPageManageBulletin',
  WRITE_PAGE_MANAGE_BULLETIN = 'writePageManageBulletin',
  READ_PAGE_MANAGE_LIBRARY = 'readPageManageLibrary',
  WRITE_PAGE_MANAGE_LIBRARY = 'writePageManageLibrary',
  READ_PAGE_MANAGE_FORMS = 'readPageManageForms',
  WRITE_PAGE_MANAGE_FORMS = 'writePageManageForms',
  READ_PAGE_MANAGE_WAITLIST = 'readPageManageWaitlist',
  WRITE_PAGE_MANAGE_WAITLIST = 'writePageManageWaitlist',
  READ_PAGE_MANAGE_CANDID_SYNC = 'readPageManageCandidSync',
  WRITE_PAGE_MANAGE_CANDID_SYNC = 'writePageManageCandidSync',
  READ_PAGE_CUSTOMER_SUCCESS_DATA = 'readPageCustomerSuccessData',
  READ_PAGE_MANAGE_FEEDBACK = 'readPageManageFeedback',
  WRITE_PAGE_MANAGE_FEEDBACK = 'writePageManageFeedback',
  READ_PAGE_MANAGE_BLOG = 'READ_PAGE_MANAGE_BLOG',
  WRITE_PAGE_MANAGE_BLOG = 'WRITE_PAGE_MANAGE_BLOG',
  READ_CONTENT_ENGINE = 'readContentEngine',
  REVIEW_CONTENT_ENGINE = 'reviewContentEngine',
  REVIEW_CONTENT_ENGINE_CLINICAL = 'reviewContentEngineClinical',
  REVIEW_CONTENT_ENGINE_INSURANCE_OPERATIONS = 'reviewContentEngineInsuranceOperations',
  REVIEW_CONTENT_ENGINE_LEGAL_COMPLIANCE = 'reviewContentEngineLegalCompliance',
  REVIEW_CONTENT_ENGINE_BRAND = 'reviewContentEngineBrand',
  REVIEW_CONTENT_ENGINE_WEBSITE_PRODUCT = 'reviewContentEngineWebsiteProduct',
  PUBLISH_CONTENT_ENGINE = 'publishContentEngine',
  MANAGE_CONTENT_ENGINE_IDENTITIES = 'manageContentEngineIdentities',
  READ_PAGE_MANAGE_FINANCIAL_TOOLKIT = 'readPageManageFinancialToolkit',
  READ_PAYMENT_CLOSE = 'readPaymentClose',
  RECONCILE_PAYMENT_CLOSE = 'reconcilePaymentClose',
  APPROVE_PAYMENT_CLOSE = 'approvePaymentClose',
  REOPEN_PAYMENT_CLOSE = 'reopenPaymentClose',
  MANUALLY_ALLOCATE_PAYMENT_CLOSE = 'manuallyAllocatePaymentClose',
  CONFIRM_PAYMENT_CLOSE_SENT = 'confirmPaymentCloseSent',
  CORRECT_PAYMENT_CLOSE = 'correctPaymentClose',
  MANAGE_PAYMENT_TERMS = 'managePaymentTerms',
  MANAGE_PAYMENT_ADVANCES = 'managePaymentAdvances',
  MANAGE_PAYMENT_ADVANCE_REQUESTS = 'managePaymentAdvanceRequests',
  APPROVE_PAYMENT_ADVANCE_EXCEPTION = 'approvePaymentAdvanceException',
  ADJUST_PAYMENT_ADVANCE_BALANCE = 'adjustPaymentAdvanceBalance',
  ADJUST_PAYMENT_FUTURE_OFFSET = 'adjustPaymentFutureOffset',

  // SUPER POWERS
  MANAGE_ADMIN = 'manageAdmin',
  MANAGE_CACHE = 'manageCache',
  MANAGE_NOTIFICATIONS = 'manageNotifications',
  PROVIDER_ACCOUNT_ACCESS = 'providerAccountAccess',
  CLIENT_ACCOUNT_ACCESS = 'clientAccountAccess',
  SEND_ALL_PROVIDER_MSGS = 'sendAllProviderMessages',
  USE_HALO_AI = 'useHaloAi',
  USE_TREATMENT_PLAN_AUDITOR = 'useTreatmentPlanAuditor',
  MANAGE_AUDIT_CHECKLISTS = 'manageAuditChecklists',
  USE_TREATMENT_AUTHORIZATION_REVIEWER = 'useTreatmentAuthorizationReviewer',
}

export type GroupedPermission = {
  read?: AdminPermission;
  write?: AdminPermission;
};

export const groupedPermissions: Record<string, GroupedPermission> = {
  'Credentialing: Review': {
    read: AdminPermission.READ_CREDENTIALING,
    write: AdminPermission.REVIEW_CREDENTIALING,
  },
  'Credentialing: Operate': {
    write: AdminPermission.OPERATE_CREDENTIALING,
  },
  'Chat: General Admin': {
    read: AdminPermission.READ_CHAT_GENERAL_ADMIN,
    write: AdminPermission.WRITE_CHAT_GENERAL_ADMIN,
  },
  'Send to All Providers': {
    write: AdminPermission.SEND_ALL_PROVIDER_MSGS,
  },
  'Chat: RBT Admin': {
    read: AdminPermission.READ_CHAT_RBT_ADMIN,
    write: AdminPermission.WRITE_CHAT_RBT_ADMIN,
  },
  'Chat: Intake Admin': {
    read: AdminPermission.READ_CHAT_CLIENT_ACQ_ADMIN,
    write: AdminPermission.WRITE_CHAT_CLIENT_ACQ_ADMIN,
  },
  'Chat: Insurance Ops Admin': {
    read: AdminPermission.READ_CHAT_INS_OPS_ADMIN,
    write: AdminPermission.WRITE_CHAT_INS_OPS_ADMIN,
  },
  'Chat: Software Admin': {
    read: AdminPermission.READ_CHAT_TECH_ADMIN,
    write: AdminPermission.WRITE_CHAT_TECH_ADMIN,
  },
  'Chat: Clinical Admin': {
    read: AdminPermission.READ_CHAT_CLINICAL_ADMIN,
    write: AdminPermission.WRITE_CHAT_CLINICAL_ADMIN,
  },
  'Chat: Client': {
    read: AdminPermission.READ_CHAT_CLIENT,
    write: AdminPermission.WRITE_CHAT_CLIENT,
  },
  'Page: Clients': {
    read: AdminPermission.READ_PAGE_MANAGE_CLIENTS,
    write: AdminPermission.WRITE_PAGE_MANAGE_CLIENTS,
  },
  'Page: Providers': {
    read: AdminPermission.READ_PAGE_MANAGE_PROVIDERS,
    write: AdminPermission.WRITE_PAGE_MANAGE_PROVIDERS,
  },
  'Page: Referrals': {
    read: AdminPermission.READ_PAGE_MANAGE_REFERRALS,
    write: AdminPermission.WRITE_PAGE_MANAGE_REFERRALS,
  },
  'Page: Auth Request': {
    read: AdminPermission.READ_PAGE_MANAGE_AUTH_REQUEST,
    write: AdminPermission.WRITE_PAGE_MANAGE_AUTH_REQUEST,
  },
  'Page: RBT Requests & Manage RBTs': {
    read: AdminPermission.READ_PAGE_MANAGE_RBT,
    write: AdminPermission.WRITE_PAGE_MANAGE_RBT,
  },
  'Page: Aloha -> Candid Apt Sync': {
    read: AdminPermission.READ_PAGE_MANAGE_CANDID_SYNC,
    write: AdminPermission.WRITE_PAGE_MANAGE_CANDID_SYNC,
  },
  'Page: Bulletin': {
    read: AdminPermission.READ_PAGE_MANAGE_BULLETIN,
    write: AdminPermission.WRITE_PAGE_MANAGE_BULLETIN,
  },
  'Page: Surveys': {
    read: AdminPermission.READ_PAGE_MANAGE_FORMS,
    write: AdminPermission.WRITE_PAGE_MANAGE_FORMS,
  },
  'Page: Feedback': {
    read: AdminPermission.READ_PAGE_MANAGE_FEEDBACK,
    write: AdminPermission.WRITE_PAGE_MANAGE_FEEDBACK,
  },
  'Page: Waitlist': {
    read: AdminPermission.READ_PAGE_MANAGE_WAITLIST,
    write: AdminPermission.WRITE_PAGE_MANAGE_WAITLIST,
  },
  'Page: Library': {
    read: AdminPermission.READ_PAGE_MANAGE_LIBRARY,
    write: AdminPermission.WRITE_PAGE_MANAGE_LIBRARY,
  },
  'Page: Blog': {
    read: AdminPermission.READ_PAGE_MANAGE_BLOG,
    write: AdminPermission.WRITE_PAGE_MANAGE_BLOG,
  },
  'Aloha Provisioning': {
    read: AdminPermission.READ_ALOHA_PROVISIONING,
    write: AdminPermission.PAUSE_ALOHA_PROVISIONING,
  },
  'Aloha Provisioning: Recovery': {
    write: AdminPermission.RECOVER_ALOHA_PROVISIONING,
  },
  'Aloha Provisioning: Assignment': { write: AdminPermission.ASSIGN_ALOHA_PROVISIONING },
  'Aloha Provisioning: Human takeover': { write: AdminPermission.TAKEOVER_ALOHA_PROVISIONING },
  'Aloha Provisioning: Audit review': {
    write: AdminPermission.REVIEW_ALOHA_PROVISIONING,
  },
  'Content Engine: Review': {
    read: AdminPermission.READ_CONTENT_ENGINE,
    write: AdminPermission.REVIEW_CONTENT_ENGINE,
  },
  'Content Engine: Clinical Review': {
    write: AdminPermission.REVIEW_CONTENT_ENGINE_CLINICAL,
  },
  'Content Engine: Insurance & Operations Review': {
    write: AdminPermission.REVIEW_CONTENT_ENGINE_INSURANCE_OPERATIONS,
  },
  'Content Engine: Legal & Compliance Review': {
    write: AdminPermission.REVIEW_CONTENT_ENGINE_LEGAL_COMPLIANCE,
  },
  'Content Engine: Brand Review': {
    write: AdminPermission.REVIEW_CONTENT_ENGINE_BRAND,
  },
  'Content Engine: Website & Product Review': {
    write: AdminPermission.REVIEW_CONTENT_ENGINE_WEBSITE_PRODUCT,
  },
  'Content Engine: Publish': {
    write: AdminPermission.PUBLISH_CONTENT_ENGINE,
  },
  'Content Engine: Service Identities': {
    write: AdminPermission.MANAGE_CONTENT_ENGINE_IDENTITIES,
  },
  'Page: Financial Toolkit': {
    read: AdminPermission.READ_PAGE_MANAGE_FINANCIAL_TOOLKIT,
  },
  'Page: Weekly Payments': {
    read: AdminPermission.READ_PAYMENT_CLOSE,
    write: AdminPermission.RECONCILE_PAYMENT_CLOSE,
  },
  'Payment Close: Approve': {
    write: AdminPermission.APPROVE_PAYMENT_CLOSE,
  },
  'Payment Close: Reopen': {
    write: AdminPermission.REOPEN_PAYMENT_CLOSE,
  },
  'Payment Close: Manual Allocation': {
    write: AdminPermission.MANUALLY_ALLOCATE_PAYMENT_CLOSE,
  },
  'Payment Close: Confirm Sent': {
    write: AdminPermission.CONFIRM_PAYMENT_CLOSE_SENT,
  },
  'Payment Close: Correct': {
    write: AdminPermission.CORRECT_PAYMENT_CLOSE,
  },
  'Payments: Manage Terms': {
    write: AdminPermission.MANAGE_PAYMENT_TERMS,
  },
  'Payments: Manage Advances': {
    write: AdminPermission.MANAGE_PAYMENT_ADVANCES,
  },
  'Payments: Manage Advance Requests and Caps': {
    write: AdminPermission.MANAGE_PAYMENT_ADVANCE_REQUESTS,
  },
  'Payments: Approve Advance Exception': {
    write: AdminPermission.APPROVE_PAYMENT_ADVANCE_EXCEPTION,
  },
  'Payments: Adjust Advance Balance': {
    write: AdminPermission.ADJUST_PAYMENT_ADVANCE_BALANCE,
  },
  'Payments: Adjust Future Offset': {
    write: AdminPermission.ADJUST_PAYMENT_FUTURE_OFFSET,
  },
  'Page: Customer Success Data': {
    read: AdminPermission.READ_PAGE_CUSTOMER_SUCCESS_DATA,
  },
  'Provider Account Access': {
    write: AdminPermission.PROVIDER_ACCOUNT_ACCESS,
  },
  'Client Account Access': {
    write: AdminPermission.CLIENT_ACCOUNT_ACCESS,
  },
  'Manage Admin Users': {
    write: AdminPermission.MANAGE_ADMIN,
  },
  'Cache Management': {
    write: AdminPermission.MANAGE_CACHE,
  },
  'Manage Notifications': {
    write: AdminPermission.MANAGE_NOTIFICATIONS,
  },
  'Halo AI': {
    write: AdminPermission.USE_HALO_AI,
  },
  'Treatment Plan Auditor': {
    write: AdminPermission.USE_TREATMENT_PLAN_AUDITOR,
  },
  'Manage Audit Checklists': {
    write: AdminPermission.MANAGE_AUDIT_CHECKLISTS,
  },
  'Treatment Authorization Reviewer': {
    write: AdminPermission.USE_TREATMENT_AUTHORIZATION_REVIEWER,
  },
};

export function getEnumKeyByEnumValue<TEnumKey extends string, TEnumVal extends string | number>(
  myEnum: { [key in TEnumKey]: TEnumVal },
  enumValue: TEnumVal
): string {
  const keys = (Object.keys(myEnum) as TEnumKey[]).filter((x) => myEnum[x] === enumValue);
  return keys.length > 0 ? keys[0] : '';
}

export const getAdminPermissionEnumByValue = (value: string) => {
  const asEnum = AdminPermission[getEnumKeyByEnumValue(AdminPermission, value)] as AdminPermission;
  return asEnum;
};

export const FULL_PERMISSIONS: AdminPermission[] = Object.values(AdminPermission);

export const CONTENT_ENGINE_DEPENDENT_PERMISSIONS: readonly AdminPermission[] = [
  AdminPermission.REVIEW_CONTENT_ENGINE,
  AdminPermission.REVIEW_CONTENT_ENGINE_CLINICAL,
  AdminPermission.REVIEW_CONTENT_ENGINE_INSURANCE_OPERATIONS,
  AdminPermission.REVIEW_CONTENT_ENGINE_LEGAL_COMPLIANCE,
  AdminPermission.REVIEW_CONTENT_ENGINE_BRAND,
  AdminPermission.REVIEW_CONTENT_ENGINE_WEBSITE_PRODUCT,
  AdminPermission.PUBLISH_CONTENT_ENGINE,
  AdminPermission.MANAGE_CONTENT_ENGINE_IDENTITIES,
];

export const CREDENTIALING_DEPENDENT_PERMISSIONS: readonly AdminPermission[] = [
  AdminPermission.REVIEW_CREDENTIALING,
  AdminPermission.OPERATE_CREDENTIALING,
];

/** Include required read access for selected actions without granting additional actions. */
export function normalizeAdminPermissions(
  permissions: readonly AdminPermission[]
): AdminPermission[] {
  const selected = new Set(permissions);
  if (CONTENT_ENGINE_DEPENDENT_PERMISSIONS.some((permission) => selected.has(permission))) {
    selected.add(AdminPermission.READ_CONTENT_ENGINE);
  }
  if (CREDENTIALING_DEPENDENT_PERMISSIONS.some((permission) => selected.has(permission))) {
    selected.add(AdminPermission.READ_CREDENTIALING);
  }
  if (
    selected.has(AdminPermission.PAUSE_ALOHA_PROVISIONING) ||
    selected.has(AdminPermission.RECOVER_ALOHA_PROVISIONING) ||
    selected.has(AdminPermission.REVIEW_ALOHA_PROVISIONING) ||
    selected.has(AdminPermission.ASSIGN_ALOHA_PROVISIONING) ||
    selected.has(AdminPermission.TAKEOVER_ALOHA_PROVISIONING)
  ) {
    selected.add(AdminPermission.READ_ALOHA_PROVISIONING);
  }
  return FULL_PERMISSIONS.filter((permission) => selected.has(permission));
}

export function addAdminPermissions(
  permissions: readonly AdminPermission[],
  additions: readonly AdminPermission[]
): AdminPermission[] {
  return normalizeAdminPermissions([...permissions, ...additions]);
}

/** Revoke dependent actions when their read access is removed, preserving unrelated grants. */
export function removeAdminPermissions(
  permissions: readonly AdminPermission[],
  removals: readonly AdminPermission[]
): AdminPermission[] {
  const permissionsToRemove = new Set(removals);
  if (permissionsToRemove.has(AdminPermission.READ_CONTENT_ENGINE)) {
    CONTENT_ENGINE_DEPENDENT_PERMISSIONS.forEach((permission) =>
      permissionsToRemove.add(permission)
    );
  }
  if (permissionsToRemove.has(AdminPermission.READ_CREDENTIALING)) {
    CREDENTIALING_DEPENDENT_PERMISSIONS.forEach((permission) =>
      permissionsToRemove.add(permission)
    );
  }
  if (permissionsToRemove.has(AdminPermission.READ_ALOHA_PROVISIONING)) {
    permissionsToRemove.add(AdminPermission.PAUSE_ALOHA_PROVISIONING);
    permissionsToRemove.add(AdminPermission.RECOVER_ALOHA_PROVISIONING);
    permissionsToRemove.add(AdminPermission.REVIEW_ALOHA_PROVISIONING);
    permissionsToRemove.add(AdminPermission.ASSIGN_ALOHA_PROVISIONING);
    permissionsToRemove.add(AdminPermission.TAKEOVER_ALOHA_PROVISIONING);
  }
  return normalizeAdminPermissions(
    permissions.filter((permission) => !permissionsToRemove.has(permission))
  );
}
